/**
 * Mutating modal hooks.
 *
 * `useModalControls` (per-modal state + open/close/getUrl) and `useOpenModal`
 * (generic opener) live here. Split out from the original `use-modal-controls.ts`
 * so the file stays under the 300-line cap. No behavior changes — same
 * callback identities, same effect deps, same return shape as before.
 */

"use client";

import { useCallback, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import {
  type ConfiguredModalId,
  type ModalParams,
  parseHubModalUrlState,
} from "../url/modal-url-config";
import { useHubModalController } from "./use-hub-modal-controller";
import type {
  ModalControlOptions,
  ModalControls,
} from "./use-modal-controls.types";

/**
 * Hook for controlling a specific modal with type-safe parameters
 *
 * Provides methods to open, close, and check the state of a modal with
 * automatic URL synchronization and TypeScript inference for parameters.
 *
 * @param modalId - The modal ID to control
 * @returns Modal control functions and state
 *
 * @example
 * ```tsx
 * function DealCard({ deal }) {
 *   const { openModal } = useModalControls('deals-detail');
 *
 *   return (
 *     <button onClick={() => openModal({ dealsId: deal.id })}>
 *       View Deal
 *     </button>
 *   );
 * }
 * ```
 */
export function useModalControls<T extends ConfiguredModalId>(
  modalId: T
): ModalControls<T> {
  const searchParams = useSearchParams();
  const controller = useHubModalController();

  // Check if modal is currently open
  const isOpen = useMemo(() => {
    return controller.isOpen(modalId);
  }, [controller, modalId]);

  // Get current entity ID from URL
  const entityId = useMemo(() => {
    const parsed = parseHubModalUrlState(searchParams);
    if (!parsed || parsed.modalId !== modalId) return null;
    return parsed.entityId;
  }, [searchParams, modalId]);

  // Get all current parameters from URL
  const params = useMemo(() => {
    const parsed = parseHubModalUrlState(searchParams);
    if (!parsed || parsed.modalId !== modalId) return null;
    return parsed.params;
  }, [searchParams, modalId]);

  /**
   * Open the modal with parameters
   */
  const openModal = useCallback(
    (modalParams: ModalParams<T> & Record<string, string | undefined>, options: ModalControlOptions = {}) => {
      controller.openModal(modalId, modalParams, options);
    },
    [controller, modalId]
  );

  /**
   * Close the modal and clean up URL
   */
  const closeModal = useCallback(
    (options: Pick<ModalControlOptions, "replace"> = {}) => {
      controller.closeModal(modalId, options);
    },
    [controller, modalId]
  );

  /**
   * Get URL for opening modal with parameters
   */
  const getUrl = useCallback(
    (modalParams: ModalParams<T> & Record<string, string | undefined>) => {
      return controller.getModalUrl(modalId, modalParams);
    },
    [controller, modalId]
  );

  return {
    openModal,
    closeModal,
    isOpen,
    getUrl,
    entityId,
    params,
  };
}

/**
 * Hook for programmatically opening any modal
 *
 * Similar to useModalControls but doesn't track state for a specific modal.
 * Useful for components that need to open multiple different modals.
 *
 * @returns Function to open any modal with parameters
 *
 * @example
 * ```tsx
 * function ActionMenu({ entity }) {
 *   const openAnyModal = useOpenModal();
 *
 *   return (
 *     <>
 *       <button onClick={() => openAnyModal('contacts-detail', { contactsId: entity.contact_id })}>
 *         View Contact
 *       </button>
 *       <button onClick={() => openAnyModal('companies-detail', { companiesId: entity.company_id })}>
 *         View Company
 *       </button>
 *     </>
 *   );
 * }
 * ```
 */
export function useOpenModal() {
  const controller = useHubModalController();

  return useCallback(
    <T extends ConfiguredModalId>(
      modalId: T,
      params: ModalParams<T> & Record<string, string | undefined>,
      options: ModalControlOptions = {}
    ) => {
      controller.openModal(modalId, params, options);
    },
    [controller]
  );
}
